import { useEffect, useMemo, useState } from 'react';
import { LineChart, type Dataset } from './LineChart';
import type { ClickStatRow } from '@zen-bell/shared';
import { adminFetch } from '../lib/api';

type ReportSettings = { time: string; enabled: boolean };

/** On/off and time of the automatic daily Teams report (Vietnam time). Saved as soon as it changes. */
function ReportTime() {
  const [settings, setSettings] = useState<ReportSettings | null>(null);
  const [status, setStatus] = useState('');

  useEffect(() => {
    adminFetch<ReportSettings>('/report/settings').then(setSettings).catch(() => setSettings(null));
  }, []);

  const save = async (changes: Partial<ReportSettings>) => {
    if (!settings || changes.time === '') return;
    setSettings({ ...settings, ...changes });
    try {
      setSettings(await adminFetch<ReportSettings>('/report/settings', { method: 'PATCH', body: JSON.stringify(changes) }));
      setStatus('✓ Saved');
    } catch (err) {
      setSettings(settings);
      setStatus((err as Error).message);
    }
    setTimeout(() => setStatus(''), 3000);
  };

  if (!settings) return null;
  return (
    <div className={`report-time${settings.enabled ? '' : ' off'}`} title="Daily report to MS Teams, Vietnam time">
      <span className="report-time-label">{status || '🕓 Daily'}</span>
      <input
        id="report-time"
        type="time"
        aria-label="Daily report time"
        value={settings.time}
        disabled={!settings.enabled}
        onChange={(e) => void save({ time: e.target.value })}
      />
      <button
        id="report-toggle"
        type="button"
        role="switch"
        aria-checked={settings.enabled}
        aria-label="Send the daily report automatically"
        className="report-switch"
        onClick={() => void save({ enabled: !settings.enabled })}
      >
        {settings.enabled ? 'On' : 'Off'}
      </button>
    </div>
  );
}

const STORE_CHANNELS = [
  { key: 'website', label: 'Website' },
  { key: 'facebook', label: 'Facebook' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'instagram', label: 'Instagram' },
] as const;

// Same ranges as the original dashboard. 400 days is what App.tsx loads.
const RANGES = [
  { days: 400, label: 'All Time' },
  { days: 1, label: 'Today' },
  { days: 3, label: 'Last 3 Days' },
  { days: 7, label: 'Last 7 Days' },
  { days: 30, label: 'Last 30 Days' },
] as const;

// Same colours and options as the original admin (Chart.js line charts).
const LINE_COLORS: Record<string, string> = {
  website: '#c9a84c',
  facebook: '#1877f2',
  youtube: '#ff0000',
  instagram: '#e1306c',
};

export function StatsDashboard({
  rows,
  onRefresh,
  onSendReport,
  sendingReport,
}: {
  rows: ClickStatRow[] | null;
  onRefresh: () => void;
  onSendReport: () => void;
  sendingReport: boolean;
}) {
  const [days, setDays] = useState<number>(400);

  const { totals, labels, viewsData, clicksData } = useMemo(() => {
    const cutoff = new Date(Date.now() - days * 86_400_000).toISOString().slice(0, 10);
    const inRange = (rows ?? []).filter((r) => r.date >= cutoff);
    const totals: Record<string, number> = {};
    const byDate = new Map<string, Record<string, number>>();
    for (const row of inRange) {
      totals[row.channel] = (totals[row.channel] ?? 0) + row.clicks;
      const day = byDate.get(row.date) ?? {};
      day[row.channel] = (day[row.channel] ?? 0) + row.clicks;
      byDate.set(row.date, day);
    }
    const labels = [...byDate.keys()].sort();
    const series = (channel: string) => labels.map((d) => byDate.get(d)?.[channel] ?? 0);
    const viewsData: Dataset[] = [
      {
        label: 'Website Views',
        data: series('pageview'),
        borderColor: '#ffffff',
        backgroundColor: 'rgba(255,255,255,0.1)',
        fill: true,
        tension: 0.3,
      },
    ];
    const clicksData: Dataset[] = STORE_CHANNELS.map(({ key, label }) => ({
      label: key === 'website' ? 'Website (Downloads)' : label,
      data: series(key),
      borderColor: LINE_COLORS[key],
      tension: 0.3,
    }));
    return { totals, labels, viewsData, clicksData };
  }, [rows, days]);

  return (
    <>
      <div className="header">
        <h2>Dashboard</h2>
        <div>
          <select
            id="date-filter"
            className="filter-select"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            aria-label="Date range"
          >
            {RANGES.map((r) => (
              <option key={r.days} value={r.days}>
                {r.label}
              </option>
            ))}
          </select>
          <button id="refresh-btn" className="btn-inline" onClick={onRefresh}>
            Refresh
          </button>
          <button
            id="send-teams-btn"
            className="btn-inline btn-outline"
            disabled={sendingReport}
            onClick={onSendReport}
            title="Send summary report to MS Teams group"
          >
            {sendingReport ? 'Sending...' : '📢 Send to Teams'}
          </button>
          <ReportTime />
        </div>
      </div>

      {rows === null ? (
        <div id="loading" className="loading">Fetching data...</div>
      ) : (
        <div id="dashboard-content">
          <h3 className="section-title">Website Traffic</h3>
          <div className="stats-grid stats-grid-single">
            <div className="stat-card" id="stat-pageview-card">
              <div className="stat-label">Website Views</div>
              <div className="stat-value" id="stat-pageview">{(totals.pageview ?? 0).toLocaleString()}</div>
            </div>
          </div>
          <LineChart labels={labels} datasets={viewsData} fill />

          <h3 className="section-title">Store Link Tracking Dashboard</h3>
          <div className="stats-grid">
            {STORE_CHANNELS.map(({ key, label }) => (
              <div className="stat-card" key={key}>
                <div className="stat-label">{label}</div>
                <div className="stat-value" id={`stat-${key}`}>{(totals[key] ?? 0).toLocaleString()}</div>
              </div>
            ))}
          </div>
          <LineChart labels={labels} datasets={clicksData} />
        </div>
      )}
    </>
  );
}
